import { getAnnualComparisonForHourlyProducts } from "$/api/market-monitoring/electricity-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import BarGraph from "$/lib/components/graphs/BarGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { Dropdown } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { ENERGY_KPIS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const YEAR_1_COLORS = ["#5978a8", "#a9cbe9"];
const YEAR_2_COLORS = ["#e68d23", "#f6be7a"];

const OPTIONS = [
  { label: "Tout", value: "TOTAL" },
  ...Object.entries(ENERGY_KPIS).map(([key, item]) => ({
    label: item.label,
    value: key,
  })),
];

const AnnualComparisonForHourlyProducts = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const currentYear = new Date().getFullYear();
  const YEARS_OPTIONS = Array.from(new Array(currentYear - 2020), (_, i) => ({
    value: currentYear - i,
    label: `${currentYear - i}`,
  })).reverse();

  const [selectedProduct, setSelectedProduct] = useState<string>("TOTAL");

  const [years, setYears] = useState({
    year1: currentYear - 1,
    year2: currentYear,
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "get-elec-annual-comparison-for-hourly-products",
      selectedProduct,
      years,
    ],
    queryFn: () =>
      getAnnualComparisonForHourlyProducts({
        selectedProduct,
        ...years,
      }),
  });

  const toDisplayYear2 = useMemo(() => (data ? data.year2Data : []), [data]);
  const toDisplayYear1 = useMemo(() => (data ? data.year1Data : []), [data]);

  const year2Keys = useMemo(
    () => getArrayKeys(toDisplayYear2, "month").sort(),
    [toDisplayYear2],
  );
  const year1Keys = useMemo(
    () => getArrayKeys(toDisplayYear1, "month").sort(),
    [toDisplayYear1],
  );

  const year2DataKeys = useMemo(
    () =>
      year2Keys.map((key, i) => ({
        key,
        color: YEAR_2_COLORS[i] ?? "",
      })),
    [year2Keys],
  );

  const year1DataKeys = useMemo(
    () =>
      year1Keys.map((key, i) => ({
        key,
        color: YEAR_1_COLORS[i] ?? "",
      })),
    [year1Keys],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  useEffect(() => {
    setGraphVisibility(
      [...year1DataKeys, ...year2DataKeys].map((i) => ({
        ...i,
        name: i.key,
      })),
    );
  }, [year2DataKeys, year1DataKeys]);

  return (
    <DisplayBox
      title="Spot moyen mensuel par année Base"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="px-2">
            <Flexbox fullWidth className="h-1/2">
              <BarGraph
                data={data?.year1Data ?? []}
                xDataKey="month"
                yAxisLabel={String(years.year1)}
                dataKeys={year1DataKeys}
                unit=" €/MWh"
                withLabel={false}
                labelPosition="insideLeft"
                // graphVisibility={graphVisibility}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-1/2">
              <BarGraph
                data={data?.year2Data ?? []}
                xDataKey="month"
                yAxisLabel={String(years.year2)}
                dataKeys={year2DataKeys}
                unit=" €/MWh"
                withLabel={false}
                labelPosition="insideLeft"
                // graphVisibility={graphVisibility}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox className="min-w-[250px]">
          <Flexbox fullWidth className="gap-2">
            <p className="whitespace-nowrap text-base font-semibold text-secondary">
              Filières
            </p>
            <Dropdown<string>
              placeholder="Filières"
              options={OPTIONS}
              defaultValue="TOTAL"
              onChange={(option) => {
                if (!option) return;
                setSelectedProduct(option.value);
              }}
              containerClassName="w-full"
            />
          </Flexbox>

          <Flexbox fullWidth className="gap-2">
            <p className="whitespace-nowrap text-base font-semibold text-secondary">
              Année 1
            </p>
            <Dropdown<number>
              options={YEARS_OPTIONS}
              placeholder="Année"
              onChange={(option) => {
                if (!option) return;
                setYears((prev) => ({
                  ...prev,
                  year1: option.value,
                }));
              }}
              value={years.year1}
              containerClassName="w-full"
            />
          </Flexbox>

          <Flexbox fullWidth className="gap-2">
            <p className="whitespace-nowrap text-base font-semibold text-secondary">
              Année 2
            </p>
            <Dropdown<number>
              options={YEARS_OPTIONS}
              placeholder="Année"
              onChange={(option) => {
                if (!option) return;
                setYears((prev) => ({
                  ...prev,
                  year2: option.value,
                }));
              }}
              value={years.year2}
              containerClassName="w-full"
            />
          </Flexbox>

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={() => {}}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default AnnualComparisonForHourlyProducts;
